import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  increment,
  writeBatch,
  deleteField,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage, appId } from '@/config/firebase';
import {
  detectFileType,
  detectFileTypeFromUrl,
  type LibraryFile,
  type FileTag,
  type FileType,
  type LibrarySortOption,
  type CreateTagInput,
  type DefaultThumbnail,
} from '@/types/library';

const CONVERTIBLE_DOC_MIME_TYPES = new Set([
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
]);
import { syncMaterialToCommunityMessage, handleMaterialDeletedFromCommunity } from './community';
// Note: Cross-user favorites sync removed - handled via lazy sync in FavoriteItemCard

// Collection paths
const getFilesPath = () => `artifacts/${appId}/public/data/files`;
const getTagsPath = () => `artifacts/${appId}/public/data/file_tags`;
const getStatsPath = () => `artifacts/${appId}/public/data/file_stats`;
const getDefaultThumbnailsPath = () => `artifacts/${appId}/public/data/default_thumbnails`;

// Storage paths
const getStoragePath = (fileName: string) => `artifacts/${appId}/library/${Date.now()}_${fileName}`;
const getThumbnailStoragePath = (fileName: string) =>
  `artifacts/${appId}/library/thumb_${Date.now()}_${fileName}`;
const getDefaultThumbnailStoragePath = (fileName: string) =>
  `artifacts/${appId}/library/default_thumb_${Date.now()}_${fileName}`;

/**
 * Remove undefined values from an object (Firestore doesn't allow undefined)
 */
function removeUndefinedValues<T extends object>(obj: T): Partial<T> {
  const cleaned: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    if (value !== undefined) {
      cleaned[key] = value;
    }
  }

  return cleaned as Partial<T>;
}

// ==================== File CRUD ====================

/**
 * Create a new library file from a URL
 */
export async function createFileFromUrl(
  title: string,
  url: string,
  userId: string,
  userName: string,
  options?: {
    description?: string;
    type?: FileType;
    tags?: string[];
    thumbnail?: File;
    defaultThumbnailId?: string;
    defaultThumbnailUrl?: string;
    wideThumbnail?: File;
    wideDefaultThumbnailId?: string;
    wideDefaultThumbnailUrl?: string;
  }
): Promise<string> {
  const filesRef = collection(db, getFilesPath());

  const fileData: Record<string, unknown> = {
    title,
    url,
    type: options?.type || detectFileTypeFromUrl(url),
    tags: options?.tags || [],
    author: userId,
    authorName: userName,
    createdAt: serverTimestamp(),
    viewCount: 0,
    downloadCount: 0,
  };

  if (options?.description) {
    fileData.description = options.description;
  }

  // Square thumbnail
  if (options?.thumbnail) {
    const thumbnailStoragePath = getThumbnailStoragePath(options.thumbnail.name);
    const thumbnailRef = ref(storage, thumbnailStoragePath);
    await uploadBytes(thumbnailRef, options.thumbnail);
    const thumbnailUrl = await getDownloadURL(thumbnailRef);
    fileData.thumbnailUrl = thumbnailUrl;
    fileData.thumbnailStoragePath = thumbnailStoragePath;
  } else if (options?.defaultThumbnailId && options?.defaultThumbnailUrl) {
    fileData.thumbnailUrl = options.defaultThumbnailUrl;
    fileData.defaultThumbnailId = options.defaultThumbnailId;
  }

  // Wide thumbnail
  if (options?.wideThumbnail) {
    const widePath = getThumbnailStoragePath(`wide_${options.wideThumbnail.name}`);
    const wideRef = ref(storage, widePath);
    await uploadBytes(wideRef, options.wideThumbnail);
    fileData.wideThumbnailUrl = await getDownloadURL(wideRef);
    fileData.wideThumbnailStoragePath = widePath;
  } else if (options?.wideDefaultThumbnailId && options?.wideDefaultThumbnailUrl) {
    fileData.wideThumbnailUrl = options.wideDefaultThumbnailUrl;
    fileData.wideDefaultThumbnailId = options.wideDefaultThumbnailId;
  }

  const docRef = await addDoc(filesRef, fileData);

  if (options?.tags && options.tags.length > 0) {
    await updateTagUsageCounts(options.tags, 1);
  }

  return docRef.id;
}

/**
 * Create a new library file by uploading to Firebase Storage
 */
export async function uploadFile(
  file: File,
  title: string,
  userId: string,
  userName: string,
  options?: {
    description?: string;
    type?: FileType;
    tags?: string[];
    thumbnail?: File;
    defaultThumbnailId?: string;
    defaultThumbnailUrl?: string;
    wideThumbnail?: File;
    wideDefaultThumbnailId?: string;
    wideDefaultThumbnailUrl?: string;
  }
): Promise<string> {
  const storagePath = getStoragePath(file.name);
  const storageRef = ref(storage, storagePath);

  await uploadBytes(storageRef, file);
  const downloadUrl = await getDownloadURL(storageRef);

  const filesRef = collection(db, getFilesPath());
  const detectedType = options?.type || detectFileType(file.type, file.name);

  const fileData: Record<string, unknown> = {
    title,
    url: downloadUrl,
    storagePath,
    fileName: file.name,
    fileSize: file.size,
    mimeType: file.type,
    type: detectedType,
    tags: options?.tags || [],
    author: userId,
    authorName: userName,
    createdAt: serverTimestamp(),
    viewCount: 0,
    downloadCount: 0,
  };

  if (detectedType === 'doc' && CONVERTIBLE_DOC_MIME_TYPES.has(file.type)) {
    fileData.conversionStatus = 'processing';
  }

  if (options?.description) {
    fileData.description = options.description;
  }

  // Square thumbnail
  if (options?.thumbnail) {
    const thumbnailStoragePath = getThumbnailStoragePath(options.thumbnail.name);
    const thumbnailRef = ref(storage, thumbnailStoragePath);
    await uploadBytes(thumbnailRef, options.thumbnail);
    fileData.thumbnailUrl = await getDownloadURL(thumbnailRef);
    fileData.thumbnailStoragePath = thumbnailStoragePath;
  } else if (options?.defaultThumbnailId && options?.defaultThumbnailUrl) {
    fileData.thumbnailUrl = options.defaultThumbnailUrl;
    fileData.defaultThumbnailId = options.defaultThumbnailId;
  }

  // Wide thumbnail
  if (options?.wideThumbnail) {
    const widePath = getThumbnailStoragePath(`wide_${options.wideThumbnail.name}`);
    const wideRef = ref(storage, widePath);
    await uploadBytes(wideRef, options.wideThumbnail);
    fileData.wideThumbnailUrl = await getDownloadURL(wideRef);
    fileData.wideThumbnailStoragePath = widePath;
  } else if (options?.wideDefaultThumbnailId && options?.wideDefaultThumbnailUrl) {
    fileData.wideThumbnailUrl = options.wideDefaultThumbnailUrl;
    fileData.wideDefaultThumbnailId = options.wideDefaultThumbnailId;
  }

  const docRef = await addDoc(filesRef, fileData);

  if (options?.tags && options.tags.length > 0) {
    await updateTagUsageCounts(options.tags, 1);
  }

  return docRef.id;
}

interface ThumbSlotInput {
  customFile?: File;
  remove?: boolean;
  defaultId?: string;
  defaultUrl?: string;
}

interface ThumbSlotResult {
  newUrl?: string;
}

async function deleteOldThumbnailIfExists(
  currentStoragePath: string | undefined,
  prefix: string
): Promise<void> {
  if (!currentStoragePath) return;
  try {
    await deleteObject(ref(storage, currentStoragePath));
  } catch (e) {
    console.error(`Failed to delete old ${prefix} thumbnail:`, e);
  }
}

async function applyThumbnailSlotUpdate(
  input: ThumbSlotInput,
  currentUrl: string | undefined,
  currentStoragePath: string | undefined,
  urlKey: string,
  storagePathKey: string,
  defaultIdKey: string,
  firestoreUpdates: Record<string, unknown>,
  prefix: string
): Promise<ThumbSlotResult> {
  if (input.customFile) {
    await deleteOldThumbnailIfExists(currentStoragePath, prefix);
    const path = getThumbnailStoragePath(`${prefix}${input.customFile.name}`);
    const tRef = ref(storage, path);
    await uploadBytes(tRef, input.customFile);
    const newUrl = await getDownloadURL(tRef);
    firestoreUpdates[urlKey] = newUrl;
    firestoreUpdates[storagePathKey] = path;
    firestoreUpdates[defaultIdKey] = null;
    return { newUrl };
  }

  if (input.defaultId && input.defaultUrl) {
    await deleteOldThumbnailIfExists(currentStoragePath, prefix);
    firestoreUpdates[urlKey] = input.defaultUrl;
    firestoreUpdates[defaultIdKey] = input.defaultId;
    firestoreUpdates[storagePathKey] = null;
    return { newUrl: input.defaultUrl };
  }

  if (input.remove && (currentStoragePath || currentUrl)) {
    await deleteOldThumbnailIfExists(currentStoragePath, prefix);
    firestoreUpdates[urlKey] = null;
    firestoreUpdates[storagePathKey] = null;
    firestoreUpdates[defaultIdKey] = null;
  }

  return {};
}

/**
 * Update a library file
 * Also syncs changes to community messages and favorites if applicable
 */
export async function updateFile(
  fileId: string,
  updates: Partial<Pick<LibraryFile, 'title' | 'description' | 'type' | 'tags'>>,
  options?: {
    thumbnail?: File;
    removeThumbnail?: boolean;
    defaultThumbnailId?: string;
    defaultThumbnailUrl?: string;
    wideThumbnail?: File;
    removeWideThumbnail?: boolean;
    wideDefaultThumbnailId?: string;
    wideDefaultThumbnailUrl?: string;
  }
): Promise<void> {
  const fileRef = doc(db, getFilesPath(), fileId);

  const currentFile = await getDoc(fileRef);
  if (!currentFile.exists()) {
    throw new Error('File not found');
  }

  const currentData = currentFile.data() as LibraryFile;

  if (updates.tags) {
    const oldTags = currentData.tags || [];
    const newTags = updates.tags;
    const removedTags = oldTags.filter((t) => !newTags.includes(t));
    const addedTags = newTags.filter((t) => !oldTags.includes(t));
    if (removedTags.length > 0) await updateTagUsageCounts(removedTags, -1);
    if (addedTags.length > 0) await updateTagUsageCounts(addedTags, 1);
  }

  const firestoreUpdates: Record<string, unknown> = { ...updates };

  const squareResult = await applyThumbnailSlotUpdate(
    {
      customFile: options?.thumbnail,
      remove: options?.removeThumbnail,
      defaultId: options?.defaultThumbnailId,
      defaultUrl: options?.defaultThumbnailUrl,
    },
    currentData.thumbnailUrl,
    currentData.thumbnailStoragePath,
    'thumbnailUrl',
    'thumbnailStoragePath',
    'defaultThumbnailId',
    firestoreUpdates,
    ''
  );

  const wideResult = await applyThumbnailSlotUpdate(
    {
      customFile: options?.wideThumbnail,
      remove: options?.removeWideThumbnail,
      defaultId: options?.wideDefaultThumbnailId,
      defaultUrl: options?.wideDefaultThumbnailUrl,
    },
    currentData.wideThumbnailUrl,
    currentData.wideThumbnailStoragePath,
    'wideThumbnailUrl',
    'wideThumbnailStoragePath',
    'wideDefaultThumbnailId',
    firestoreUpdates,
    'wide_'
  );

  const cleanedUpdates = removeUndefinedValues(firestoreUpdates);

  await updateDoc(fileRef, {
    ...cleanedUpdates,
    updatedAt: serverTimestamp(),
  });

  const updatedFile: LibraryFile = {
    ...currentData,
    ...updates,
    id: fileId,
    thumbnailUrl:
      squareResult.newUrl ?? (options?.removeThumbnail ? undefined : currentData.thumbnailUrl),
    wideThumbnailUrl:
      wideResult.newUrl ??
      (options?.removeWideThumbnail ? undefined : currentData.wideThumbnailUrl),
  };

  if (currentData.communityMessageId) {
    try {
      await syncMaterialToCommunityMessage(updatedFile);
    } catch (error) {
      console.error('Failed to sync material to community message:', error);
    }
  }
}

async function deleteStorageAssets(file: LibraryFile): Promise<void> {
  if (file.storagePath) {
    try {
      await deleteObject(ref(storage, file.storagePath));
    } catch (error) {
      console.error('Failed to delete storage file:', error);
    }
  }
  for (const path of [file.thumbnailStoragePath, file.wideThumbnailStoragePath]) {
    if (path) {
      try {
        await deleteObject(ref(storage, path));
      } catch (error) {
        console.error('Failed to delete thumbnail:', error);
      }
    }
  }
}

async function updateTagUsageOnDelete(file: LibraryFile): Promise<void> {
  if (file.tags && file.tags.length > 0) {
    await updateTagUsageCounts(file.tags, -1);
  }
}

async function cleanupCommunityMessage(file: LibraryFile): Promise<void> {
  if (!file.communityMessageId) return;
  try {
    await handleMaterialDeletedFromCommunity(file.communityMessageId, true);
  } catch (error) {
    console.error('Failed to cleanup community message:', error);
  }
}

/**
 * Delete a library file
 * Also cleans up community messages and favorites that reference this file
 */
export async function deleteFile(fileId: string): Promise<void> {
  const fileRef = doc(db, getFilesPath(), fileId);
  const fileSnap = await getDoc(fileRef);

  if (!fileSnap.exists()) {
    throw new Error('File not found');
  }

  const fileData = fileSnap.data() as LibraryFile;

  await deleteStorageAssets(fileData);
  await updateTagUsageOnDelete(fileData);
  await cleanupCommunityMessage(fileData);

  await deleteDoc(fileRef);

  try {
    await deleteDoc(doc(db, getStatsPath(), fileId));
  } catch {
    // Stats might not exist, ignore
  }
}

/**
 * Get a single file by ID
 */
export async function getFile(fileId: string): Promise<LibraryFile | null> {
  const fileRef = doc(db, getFilesPath(), fileId);
  const fileSnap = await getDoc(fileRef);

  if (!fileSnap.exists()) {
    return null;
  }

  return { id: fileSnap.id, ...fileSnap.data() } as LibraryFile;
}

/**
 * Get multiple files by IDs (batch fetch)
 * Returns files in the same order as the input IDs
 * Missing files are excluded from the result
 */
export async function getFilesByIds(fileIds: string[]): Promise<LibraryFile[]> {
  if (fileIds.length === 0) {
    return [];
  }

  // Fetch all files in parallel
  const filePromises = fileIds.map((id) => getFile(id));
  const files = await Promise.all(filePromises);

  // Filter out nulls (missing files) and return
  return files.filter((file): file is LibraryFile => file !== null);
}

/**
 * Get all library files
 */
export async function getFiles(sortBy: LibrarySortOption = 'date_desc'): Promise<LibraryFile[]> {
  const filesRef = collection(db, getFilesPath());

  let q;
  switch (sortBy) {
    case 'date_asc':
      q = query(filesRef, orderBy('createdAt', 'asc'));
      break;
    case 'title_asc':
      q = query(filesRef, orderBy('title', 'asc'));
      break;
    case 'title_desc':
      q = query(filesRef, orderBy('title', 'desc'));
      break;
    case 'views_desc':
      q = query(filesRef, orderBy('viewCount', 'desc'));
      break;
    case 'downloads_desc':
      q = query(filesRef, orderBy('downloadCount', 'desc'));
      break;
    case 'date_desc':
    default:
      q = query(filesRef, orderBy('createdAt', 'desc'));
  }

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as LibraryFile);
}

/**
 * Get files by type
 */
export async function getFilesByType(type: FileType): Promise<LibraryFile[]> {
  const filesRef = collection(db, getFilesPath());
  const q = query(filesRef, where('type', '==', type), orderBy('createdAt', 'desc'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as LibraryFile);
}

/**
 * Get files by tag
 */
export async function getFilesByTag(tagId: string): Promise<LibraryFile[]> {
  const filesRef = collection(db, getFilesPath());
  const q = query(filesRef, where('tags', 'array-contains', tagId), orderBy('createdAt', 'desc'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as LibraryFile);
}

/**
 * Subscribe to library files (real-time)
 */
export function subscribeToFiles(
  callback: (files: LibraryFile[]) => void,
  sortBy: LibrarySortOption = 'date_desc'
): () => void {
  const filesRef = collection(db, getFilesPath());

  let q;
  switch (sortBy) {
    case 'date_asc':
      q = query(filesRef, orderBy('createdAt', 'asc'));
      break;
    case 'title_asc':
      q = query(filesRef, orderBy('title', 'asc'));
      break;
    case 'title_desc':
      q = query(filesRef, orderBy('title', 'desc'));
      break;
    case 'views_desc':
      q = query(filesRef, orderBy('viewCount', 'desc'));
      break;
    case 'downloads_desc':
      q = query(filesRef, orderBy('downloadCount', 'desc'));
      break;
    case 'date_desc':
    default:
      q = query(filesRef, orderBy('createdAt', 'desc'));
  }

  return onSnapshot(q, (snapshot) => {
    const files = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as LibraryFile);
    callback(files);
  });
}

// ==================== Tag Management ====================

/**
 * Create a new tag
 */
export async function createTag(
  input: CreateTagInput,
  userId: string,
  userName: string
): Promise<string> {
  const tagsRef = collection(db, getTagsPath());

  // Check if tag name already exists
  const existingQ = query(tagsRef, where('name', '==', input.name.toLowerCase()));
  const existingSnap = await getDocs(existingQ);

  if (!existingSnap.empty) {
    throw new Error('Tag with this name already exists');
  }

  const tagData: Record<string, unknown> = {
    name: input.name.toLowerCase(),
    usageCount: 0,
    createdAt: serverTimestamp(),
    createdBy: userId,
    createdByName: userName,
  };

  // Only include optional fields if they have values
  if (input.color) {
    tagData.color = input.color;
  }

  const docRef = await addDoc(tagsRef, tagData);
  return docRef.id;
}

/**
 * Update a tag
 */
export async function updateTag(
  tagId: string,
  updates: Partial<Pick<FileTag, 'name' | 'color'>>
): Promise<void> {
  const tagRef = doc(db, getTagsPath(), tagId);

  if (updates.name) {
    updates.name = updates.name.toLowerCase();
  }

  await updateDoc(tagRef, updates);
}

/**
 * Delete a tag
 */
export async function deleteTag(tagId: string): Promise<void> {
  // Remove tag from all files that use it
  const filesWithTag = await getFilesByTag(tagId);

  for (const file of filesWithTag) {
    const newTags = file.tags.filter((t) => t !== tagId);
    await updateDoc(doc(db, getFilesPath(), file.id), { tags: newTags });
  }

  // Delete the tag
  await deleteDoc(doc(db, getTagsPath(), tagId));
}

/**
 * Get all tags
 */
export async function getTags(): Promise<FileTag[]> {
  const tagsRef = collection(db, getTagsPath());
  const q = query(tagsRef, orderBy('name', 'asc'));

  const snapshot = await getDocs(q);
  return snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as FileTag);
}

/**
 * Subscribe to tags (real-time)
 */
export function subscribeToTags(callback: (tags: FileTag[]) => void): () => void {
  const tagsRef = collection(db, getTagsPath());
  const q = query(tagsRef, orderBy('name', 'asc'));

  return onSnapshot(q, (snapshot) => {
    const tags = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }) as FileTag);
    callback(tags);
  });
}

/**
 * Update usage counts for multiple tags
 */
async function updateTagUsageCounts(tagIds: string[], delta: number): Promise<void> {
  for (const tagId of tagIds) {
    try {
      const tagRef = doc(db, getTagsPath(), tagId);
      await updateDoc(tagRef, {
        usageCount: increment(delta),
      });
    } catch (error) {
      console.error(`Failed to update usage count for tag ${tagId}:`, error);
    }
  }
}

// ==================== Stats Tracking ====================

/**
 * Track a file view
 */
export async function trackFileView(fileId: string): Promise<void> {
  const fileRef = doc(db, getFilesPath(), fileId);

  await updateDoc(fileRef, {
    viewCount: increment(1),
  });
}

/**
 * Track a file download
 */
export async function trackFileDownload(fileId: string): Promise<void> {
  const fileRef = doc(db, getFilesPath(), fileId);

  await updateDoc(fileRef, {
    downloadCount: increment(1),
  });
}

// ==================== Featured ====================

/**
 * Set a file as the featured material.
 * Clears any previously-featured file in the same batch write.
 */
export async function setFeaturedFile(fileId: string, userId: string): Promise<void> {
  const batch = writeBatch(db);
  const filesRef = collection(db, getFilesPath());

  // Find any currently-featured files and clear them
  const featuredQuery = query(filesRef, where('featuredAt', '!=', null));
  const snapshot = await getDocs(featuredQuery);
  snapshot.docs.forEach((d) => {
    if (d.id !== fileId) {
      batch.update(d.ref, { featuredAt: deleteField(), featuredBy: deleteField() });
    }
  });

  // Set the new featured file
  const fileRef = doc(db, getFilesPath(), fileId);
  batch.update(fileRef, {
    featuredAt: serverTimestamp(),
    featuredBy: userId,
  });

  await batch.commit();
}

/**
 * Remove the featured flag from a file.
 */
export async function clearFeaturedFile(fileId: string): Promise<void> {
  const fileRef = doc(db, getFilesPath(), fileId);
  await updateDoc(fileRef, {
    featuredAt: deleteField(),
    featuredBy: deleteField(),
  });
}

// ==================== Default Thumbnails ====================

/**
 * Subscribe to default thumbnails (real-time), ordered by fileType then createdAt
 */
export function subscribeToDefaultThumbnails(
  callback: (thumbnails: DefaultThumbnail[]) => void
): () => void {
  const thumbsRef = collection(db, getDefaultThumbnailsPath());
  const q = query(thumbsRef, orderBy('fileType', 'asc'), orderBy('createdAt', 'asc'));

  return onSnapshot(q, (snapshot) => {
    const thumbnails = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }) as DefaultThumbnail);
    callback(thumbnails);
  });
}

/**
 * Upload a new default thumbnail image and create its Firestore doc
 */
export async function uploadDefaultThumbnail(
  file: File,
  name: string,
  fileType: FileType,
  aspectRatio: 'square' | 'wide',
  userId: string,
  userName: string
): Promise<string> {
  const storagePath = getDefaultThumbnailStoragePath(file.name);
  const storageRef = ref(storage, storagePath);
  await uploadBytes(storageRef, file);
  const imageUrl = await getDownloadURL(storageRef);

  const thumbsRef = collection(db, getDefaultThumbnailsPath());
  const docRef = await addDoc(thumbsRef, {
    name,
    fileType,
    aspectRatio,
    imageUrl,
    storagePath,
    createdAt: serverTimestamp(),
    createdBy: userId,
    createdByName: userName,
  });

  return docRef.id;
}

/**
 * Update a default thumbnail's metadata (name or fileType)
 */
export async function updateDefaultThumbnail(
  id: string,
  updates: Partial<Pick<DefaultThumbnail, 'name' | 'fileType' | 'aspectRatio'>>
): Promise<void> {
  const thumbRef = doc(db, getDefaultThumbnailsPath(), id);
  await updateDoc(thumbRef, updates);
}

/**
 * Delete a default thumbnail from Storage and Firestore
 */
export async function deleteDefaultThumbnail(id: string, storagePath: string): Promise<void> {
  try {
    const storageRef = ref(storage, storagePath);
    await deleteObject(storageRef);
  } catch (error) {
    console.error('Failed to delete default thumbnail from storage:', error);
  }

  await deleteDoc(doc(db, getDefaultThumbnailsPath(), id));
}

// ==================== Search ====================

/**
 * Search files by title (client-side filtering)
 * For more advanced search, consider Algolia or similar
 */
export function searchFiles(files: LibraryFile[], query: string): LibraryFile[] {
  const lowerQuery = query.toLowerCase().trim();

  if (!lowerQuery) {
    return files;
  }

  return files.filter((file) => {
    const title = file.title.toLowerCase();
    const description = file.description?.toLowerCase() || '';
    const fileName = file.fileName?.toLowerCase() || '';
    const authorName = file.authorName?.toLowerCase() || '';

    return (
      title.includes(lowerQuery) ||
      description.includes(lowerQuery) ||
      fileName.includes(lowerQuery) ||
      authorName.includes(lowerQuery)
    );
  });
}

/**
 * Filter files by type
 */
// Known file types for filtering
const KNOWN_FILE_TYPES: FileType[] = ['pdf', 'doc', 'video', 'audio', 'link', 'image', 'other'];

export function filterFilesByType(files: LibraryFile[], type: FileType | 'all'): LibraryFile[] {
  if (type === 'all') {
    return files;
  }

  // If filtering by 'other', also include any unknown legacy types (like 'document')
  if (type === 'other') {
    return files.filter(
      (file) => file.type === 'other' || !KNOWN_FILE_TYPES.includes(file.type as FileType)
    );
  }

  // Filtering by 'pdf' also captures 'doc' files (transient pre-conversion state)
  if (type === 'pdf') {
    return files.filter((file) => file.type === 'pdf' || file.type === 'doc');
  }

  return files.filter((file) => file.type === type);
}

/**
 * Filter files by tags
 */
export function filterFilesByTags(files: LibraryFile[], tagIds: string[]): LibraryFile[] {
  if (tagIds.length === 0) {
    return files;
  }
  return files.filter((file) => tagIds.some((tagId) => (file.tags || []).includes(tagId)));
}

/**
 * Sort files
 */
export function sortFiles(files: LibraryFile[], sortBy: LibrarySortOption): LibraryFile[] {
  const sorted = [...files];

  switch (sortBy) {
    case 'date_asc':
      sorted.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return aTime - bTime;
      });
      break;
    case 'date_desc':
      sorted.sort((a, b) => {
        const aTime = a.createdAt?.toMillis?.() || 0;
        const bTime = b.createdAt?.toMillis?.() || 0;
        return bTime - aTime;
      });
      break;
    case 'title_asc':
      sorted.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
      break;
    case 'title_desc':
      sorted.sort((a, b) => (b.title || '').localeCompare(a.title || ''));
      break;
    case 'views_desc':
      sorted.sort((a, b) => (b.viewCount || 0) - (a.viewCount || 0));
      break;
    case 'downloads_desc':
      sorted.sort((a, b) => (b.downloadCount || 0) - (a.downloadCount || 0));
      break;
  }

  return sorted;
}
